import GetCalendarResult from 'src/models/accounting-calendar/GetCalendarResult';

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Quarter-end close months (1-based): January, April, July, October. These run the longer
 * Quarter-End Close (QEC) window; all other months run the normal Month-End Close (MEC).
 * Kept in sync with the backend's QUARTER_END_MONTHS in GetCalendarComponent.
 */
const QUARTER_END_CLOSE_MONTHS = [1, 4, 7, 10];

/**
 * Timezone the accounting calendar operates in. The backend anchors "today", the close windows,
 * and the current year to US Pacific (America/Los_Angeles), so the label must resolve "today" in
 * the same zone — not the viewer's local zone — otherwise a non-Pacific user near midnight would
 * see the wrong accounting day (e.g. "Day 2" locally when it's still "Day 1" in Pacific).
 */
const ACCOUNTING_TIME_ZONE = 'America/Los_Angeles';

/** Today's date in the accounting timezone (US Pacific), formatted as "YYYY-MM-DD". */
export function accountingTodayIso(today: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: ACCOUNTING_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(today);
}

/**
 * Derives the dynamic "Day -8 of May MEC" / "Day 3 of July QEC" label from the calendar
 * response, by looking up today's date (in US Pacific, the accounting zone) and reading its
 * mecDay (positive in-window, negative post-close). The close-type suffix is QEC for quarter-end
 * close months (Jan/Apr/Jul/Oct) and MEC otherwise. Returns '' when today's MEC day is
 * unavailable (non-working day, or data not loaded).
 *
 * @param calendar the GET /calendar response.
 * @param today    the instant to resolve; defaults to now. (Injectable for testing.)
 */
export function deriveMecDayLabel(calendar: GetCalendarResult | null | undefined, today: Date = new Date()): string {
    if (!calendar || !calendar.months) {
        return '';
    }
    // Resolve "today" in the accounting timezone (Pacific), formatted as YYYY-MM-DD (en-CA).
    const todayIso = accountingTodayIso(today);
    const monthKey = todayIso.slice(0, 7); // "YYYY-MM"
    const month = Number(todayIso.slice(5, 7));
    const monthDays = calendar.months[monthKey] || [];
    const todayData = monthDays.find(d => d.date === todayIso);
    if (!todayData || todayData.mecDay === undefined || todayData.mecDay === null) {
        return '';
    }
    const closeType = QUARTER_END_CLOSE_MONTHS.includes(month) ? 'QEC' : 'MEC';
    return `Day ${todayData.mecDay} of ${MONTH_NAMES[month - 1]} ${closeType}`;
}
